/**
 * ActivityTable Component
 *
 * Uses DataGrid for virtualized rendering. When groupBy === 'wbs', shows P6-style
 * in-table WBS grouping: collapsible WBS group rows with expand/collapse all and
 * per-row toggle; no left rail.
 */

export type TreeRow =
  | { type: 'wbs'; id: string; node: WBSNode; level: number }
  | { type: 'activity'; id: string; activity: ScheduleActivity; level: number };

import React, { useState, useMemo, useCallback, useEffect, useTransition, useRef } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Settings, Settings2, Filter, Maximize2, Minimize2, ChevronRight, ChevronDown, Download } from 'lucide-react';
import { ScheduleActivity, ACTIVITY_COLUMNS } from '@/types';
import { ActivityTableProps, ColumnState, FilterState, SearchStates, TableSettings, LayoutState } from './types';
import { DataGrid } from '@/components/data-grid';
import { useSettings } from '@/contexts/SettingsContext';
import { cn } from '@/lib/utils';
import { makeGetCellValue, getCellClassName, type FormatterConfig } from './activityFormatters';
import { PanelLoading } from '@/components/ui/loading';
import ColumnSettings from './ColumnSettings';
import FilterPanel from './FilterPanel';
import { DEFAULT_LAYOUT } from './activityLayoutDefaults';
import { loadLayout, useActivityLayout, getActiveLayoutName } from '@/hooks/useActivityLayout';
import { LayoutButton } from './LayoutButton';

interface WBSNode {
  key: string;
  name: string;
  wbsId: string;
  parentWbsId: string | null | undefined;
  level: number;
  activities: ScheduleActivity[];
  children: WBSNode[];
  rollupCount?: number;
}

function getActivitiesInSubtree(node: WBSNode): ScheduleActivity[] {
  const list = [...node.activities];
  node.children.forEach((child) => list.push(...getActivitiesInSubtree(child)));
  return list;
}

/** Flatten WBS tree to table rows: WBS group rows + activity rows (P6-style collapsible). */
function flattenTreeToRows(nodes: WBSNode[], expandedIds: Set<string>): TreeRow[] {
  const out: TreeRow[] = [];
  function visit(node: WBSNode) {
    out.push({ type: 'wbs', id: `wbs-${node.key}`, node, level: node.level });
    if (expandedIds.has(node.key)) {
      for (const activity of node.activities) {
        if (activity != null && activity.id != null) {
          out.push({
            type: 'activity',
            id: `activity-${activity.id}`,
            activity,
            level: node.level,
          });
        }
      }
      node.children.forEach(visit);
    }
  }
  nodes.forEach(visit);
  return out;
}

function isTreeRow(row: unknown): row is TreeRow {
  return typeof row === 'object' && row !== null && ((row as { type?: string }).type === 'wbs' || (row as { type?: string }).type === 'activity');
}

function collectAllWbsKeys(nodes: WBSNode[]): Set<string> {
  const keys = new Set<string>();
  function visit(node: WBSNode) {
    keys.add(node.key);
    node.children.forEach(visit);
  }
  nodes.forEach(visit);
  return keys;
}

export function ActivityTable({
  activities,
  allWBSNodes = [],
  loading = false,
  groupBy: initialGroupBy = 'none',
  udfLabels = {},
  onActivityClick,
  title: _toolbarTitle = 'Activities',
  toolbarSubtitle: _toolbarSubtitle,
  scrollPersistKey,
}: ActivityTableProps) {
  const { settings } = useSettings();
  // Detect UDF fields from activities and merge with predefined columns
  const allColumns = useMemo(() => {
    const predefinedKeys = new Set(ACTIVITY_COLUMNS.map(col => col.key))
    const udfColumns: typeof ACTIVITY_COLUMNS = []
    
    // Find UDF fields in activity data
    // Exclude 'udf_values' (raw concatenated string) and only include actual UDF fields
    if (activities.length > 0) {
      const firstActivity = activities[0] as any
      Object.keys(firstActivity).forEach(key => {
        const keyLower = key.toLowerCase()
        // UDF fields should be named like 'udf_130', 'udf_134', etc. (udf_{udf_type_id})
        // Exclude 'udf_values' which is the raw concatenated string
        if ((keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) && 
            !predefinedKeys.has(key as keyof ScheduleActivity) &&
            keyLower !== 'udf_values') {
          // Extract UDF type ID from field name (e.g., 'udf_130' -> '130')
          const udfTypeId = key.replace(/^udf_?/i, '')
          // Use UDF label if available, add "(UDF)" suffix, otherwise fall back to "UDF {id}"
          const displayLabel = udfLabels[udfTypeId] 
            ? `${udfLabels[udfTypeId]} (UDF)`
            : (udfTypeId ? `UDF ${udfTypeId}` : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
          
          udfColumns.push({
            key: key as keyof ScheduleActivity,
            label: displayLabel,
            description: `User Defined Field: ${key}`,
            category: 'udf' as const,
            defaultVisible: false,
            sortable: true,
            width: '150px'
          })
        }
      })
    }
    
    return [...ACTIVITY_COLUMNS, ...udfColumns]
  }, [activities, udfLabels])

  const [columnState, setColumnState] = useState<ColumnState[]>(() =>
    DEFAULT_LAYOUT.columnState.map((c) => ({ ...c }))
  )

  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(() => ({ ...DEFAULT_LAYOUT.columnSizing }))
  const [tableSettings, setTableSettings] = useState<TableSettings>(() => ({ ...DEFAULT_LAYOUT.tableSettings }))
  const [floatDisplayUnit, setFloatDisplayUnit] = useState<'hours' | 'days'>(DEFAULT_LAYOUT.floatDisplayUnit)

  const layoutHydratedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!scrollPersistKey) {
      layoutHydratedRef.current = null
      return
    }
    if (layoutHydratedRef.current === scrollPersistKey) return
    layoutHydratedRef.current = scrollPersistKey
    const loaded = loadLayout(scrollPersistKey)
    if (loaded) {
      setColumnState(loaded.columnState.map((c) => ({ ...c })))
      setColumnSizing({ ...loaded.columnSizing })
      setGroupBy(loaded.groupBy)
      setTableSettings({ ...loaded.tableSettings })
      setFloatDisplayUnit(loaded.floatDisplayUnit)
    }
  }, [scrollPersistKey])

  // Update columnState when new UDF fields are detected from activities (use prev in updater to avoid loop).
  useEffect(() => {
    if (activities.length === 0) return

    const detectedUdfKeys = new Set<string>()
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        const keyLower = key.toLowerCase()
        if ((keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
            !ACTIVITY_COLUMNS.some(col => col.key === key) &&
            keyLower !== 'udf_values') {
          detectedUdfKeys.add(key)
        }
      })
    })

    setColumnState(prev => {
      const currentKeys = new Set(prev.map(col => col.key))
      const newUdfKeys = Array.from(detectedUdfKeys).filter(key => !currentKeys.has(key))
      if (newUdfKeys.length === 0) return prev
      return [
        ...prev,
        ...newUdfKeys.map((key, index) => ({
          key: String(key),
          visible: false,
          order: prev.length + index,
          decimalsEnabled: false,
          decimalPlaces: 2
        }))
      ]
    })
  }, [activities])
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const toolbarActionsRef = useRef<{ onCopy: () => void; onCsvExport: () => void } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    dateRange: { start: '', end: '' },
    resources: [],
    wbs: [],
    calendars: [],
    types: [],
    statuses: [],
    udf: [],
    criticalOnly: false
  })

  const [searchStates, setSearchStates] = useState<SearchStates>({
    wbs: '',
    calendars: '',
    types: '',
    statuses: '',
    udf: ''
  })

  const [groupBy, setGroupBy] = useState<'none' | 'wbs'>(initialGroupBy);
  const [expandedWbsIds, setExpandedWbsIds] = useState<Set<string>>(() => new Set());
  const prevGroupByRef = React.useRef<'none' | 'wbs'>(groupBy);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [, startTransition] = useTransition();

  const layoutSnapshot = useMemo(
    () => ({
      columnState,
      columnSizing,
      groupBy,
      tableSettings,
      floatDisplayUnit,
    }),
    [columnState, columnSizing, groupBy, tableSettings, floatDisplayUnit]
  );

  useActivityLayout({
    scope: scrollPersistKey,
    state: layoutSnapshot,
    skipAutoSave: scrollPersistKey ? getActiveLayoutName(scrollPersistKey) !== 'auto' : true,
  });

  const handleApplyLayout = useCallback((state: LayoutState) => {
    setColumnState(state.columnState.map((c) => ({ ...c })));
    setColumnSizing({ ...state.columnSizing });
    setGroupBy(state.groupBy);
    setTableSettings({ ...state.tableSettings });
    setFloatDisplayUnit(state.floatDisplayUnit);
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isFullscreen]);

  // Filter out the percent_complete_type column since we're handling it differently
  const filteredColumns = allColumns.filter(col => col.key !== 'percent_complete_type')

  // Get visible columns in order. Activity ID (id) is always visible and cannot be hidden.
  const visibleColumns = useMemo(() => {
    const visible = columnState
      .filter(col => (col.visible || col.key === 'id') && col.key !== 'percent_complete_type')
      .sort((a, b) => a.order - b.order)
      .map(col => {
        const columnDef = filteredColumns.find(c => c.key === col.key)
        return columnDef || {
          key: col.key,
          label: String(col.key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          description: '',
          category: 'advanced' as const,
          defaultVisible: false,
          sortable: true
        }
      })
      .filter(Boolean)
    
    return visible
  }, [columnState, filteredColumns])

  // Filter activities (search + comprehensive filters). Sorting is handled by DataGrid.
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    if (searchTerm) {
      filtered = activities.filter((activity) =>
        Object.values(activity).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    filtered = filtered.filter((activity) => {
      if (filters.criticalOnly && !activity.critical) return false;
      if (filters.dateRange.start && activity.start_date && activity.start_date < filters.dateRange.start) return false;
      if (filters.dateRange.end && activity.end_date && activity.end_date > filters.dateRange.end) return false;
      if (filters.wbs.length > 0 && activity.wbs_name && !filters.wbs.includes(activity.wbs_name)) return false;
      if (filters.calendars.length > 0 && activity.calendar_name && !filters.calendars.includes(activity.calendar_name)) return false;
      if (filters.types.length > 0 && activity.type && !filters.types.includes(activity.type)) return false;
      if (filters.statuses.length > 0 && activity.status && !filters.statuses.includes(activity.status)) return false;
      if (filters.udf.length > 0) {
        const hasMatchingUdf = Object.keys(activity).some((key) => {
          const keyLower = key.toLowerCase();
          if (
            (keyLower.startsWith('udf_') || (keyLower.startsWith('udf') && keyLower !== 'udf_values')) &&
            keyLower !== 'udf_values' &&
            !ACTIVITY_COLUMNS.some((col) => col.key === key)
          ) {
            const value = (activity as Record<string, unknown>)[key];
            return value && filters.udf.includes(String(value));
          }
          return false;
        });
        if (!hasMatchingUdf) return false;
      }
      return true;
    });

    return filtered;
  }, [activities, searchTerm, filters]);

  const wbsTreeStructure = useMemo(() => {
    if (groupBy !== 'wbs') return { wbsMap: new Map<string, WBSNode>(), rootNodes: [] as WBSNode[] };

    const normalizeWbsId = (id: string | number | null | undefined): string =>
      id ? String(id).trim() : '';

    const wbsMap = new Map<string, WBSNode>();
    const rootNodes: WBSNode[] = [];

    if (allWBSNodes.length > 0) {
      allWBSNodes.forEach((wbsItem: { wbs_id?: string; wbs_name?: string; parent_wbs_id?: string }) => {
        const wbsId = normalizeWbsId(wbsItem.wbs_id);
        const wbsName = wbsItem.wbs_name || 'No WBS';
        const parentWbsId = normalizeWbsId(wbsItem.parent_wbs_id) || null;
        if (wbsId && !wbsMap.has(wbsId)) {
          wbsMap.set(wbsId, {
            key: wbsId,
            name: wbsName,
            wbsId,
            parentWbsId,
            level: 0,
            activities: [],
            children: [],
          });
        }
      });
      filteredActivities.forEach((activity) => {
        const wbsId = normalizeWbsId(activity.wbs_id);
        if (wbsId && wbsMap.has(wbsId)) {
          wbsMap.get(wbsId)!.activities.push(activity);
        } else if (wbsId) {
          wbsMap.set(wbsId, {
            key: wbsId,
            name: activity.wbs_name || 'No WBS',
            wbsId,
            parentWbsId: normalizeWbsId(activity.parent_wbs_id) || null,
            level: 0,
            activities: [activity],
            children: [],
          });
        } else {
          const nodeKey = 'NO_WBS';
          if (!wbsMap.has(nodeKey)) {
            wbsMap.set(nodeKey, {
              key: nodeKey,
              name: 'No WBS',
              wbsId: '',
              parentWbsId: null,
              level: 0,
              activities: [],
              children: [],
            });
          }
          wbsMap.get(nodeKey)!.activities.push(activity);
        }
      });
    } else {
      filteredActivities.forEach((activity) => {
        const wbsId = normalizeWbsId(activity.wbs_id);
        const wbsName = activity.wbs_name || 'No WBS';
        const parentWbsId = normalizeWbsId(activity.parent_wbs_id) || null;
        if (!wbsId) {
          const nodeKey = wbsName;
          if (!wbsMap.has(nodeKey)) {
            wbsMap.set(nodeKey, {
              key: nodeKey,
              name: wbsName,
              wbsId: '',
              parentWbsId: null,
              level: 0,
              activities: [],
              children: [],
            });
          }
          wbsMap.get(nodeKey)!.activities.push(activity);
        } else {
          if (!wbsMap.has(wbsId)) {
            wbsMap.set(wbsId, {
              key: wbsId,
              name: wbsName,
              wbsId,
              parentWbsId,
              level: 0,
              activities: [],
              children: [],
            });
          }
          wbsMap.get(wbsId)!.activities.push(activity);
        }
      });
    }

    wbsMap.forEach((node) => {
      if (node.parentWbsId) {
        const parent = wbsMap.get(node.parentWbsId);
        if (parent) {
          parent.children.push(node);
          return;
        }
      }
      rootNodes.push(node);
    });

    const setLevels = (nodes: WBSNode[], level: number): void => {
      nodes.forEach((n) => {
        n.level = level;
        setLevels(n.children, level + 1);
      });
    };
    setLevels(rootNodes, 0);

    const sortNodes = (nodes: WBSNode[]): WBSNode[] =>
      nodes
        .sort((a, b) => (a.level !== b.level ? a.level - b.level : a.name.localeCompare(b.name)))
        .map((node) => ({ ...node, children: sortNodes(node.children) }));

    const calculateRollupCount = (node: WBSNode): number => {
      if (node.rollupCount !== undefined) return node.rollupCount;
      let count = node.activities.length;
      node.children.forEach((child) => {
        count += calculateRollupCount(child);
      });
      node.rollupCount = count;
      return count;
    };

    const sortedRootNodes = sortNodes(rootNodes);
    sortedRootNodes.forEach((root) => calculateRollupCount(root));
    return { wbsMap, rootNodes: sortedRootNodes };
  }, [groupBy, filteredActivities, allWBSNodes]);

  // When switching to Group by WBS, start with expand all
  useEffect(() => {
    if (groupBy === 'wbs' && prevGroupByRef.current !== 'wbs' && wbsTreeStructure.rootNodes.length > 0) {
      setExpandedWbsIds(collectAllWbsKeys(wbsTreeStructure.rootNodes));
      prevGroupByRef.current = 'wbs';
    }
    if (groupBy !== 'wbs') prevGroupByRef.current = groupBy;
  }, [groupBy, wbsTreeStructure.rootNodes]);

  const allWbsKeys = useMemo(
    () => (groupBy === 'wbs' ? collectAllWbsKeys(wbsTreeStructure.rootNodes) : new Set<string>()),
    [groupBy, wbsTreeStructure.rootNodes]
  );

  const toggleWbsExpanded = useCallback((key: string) => {
    setExpandedWbsIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAllWbs = useCallback(() => {
    setExpandedWbsIds(new Set(allWbsKeys));
  }, [allWbsKeys]);

  const collapseAllWbs = useCallback(() => {
    setExpandedWbsIds(new Set());
  }, []);

  const gridData = useMemo((): TreeRow[] | ScheduleActivity[] => {
    if (groupBy !== 'wbs') return filteredActivities;
    return flattenTreeToRows(wbsTreeStructure.rootNodes, expandedWbsIds);
  }, [groupBy, filteredActivities, wbsTreeStructure.rootNodes, expandedWbsIds]);

  const formatterConfig: FormatterConfig = useMemo(
    () => ({
      dateFormat: settings.dateFormat,
      timeZone: settings.timezone,
      columnState,
      showTime: tableSettings.showTime,
      floatDisplayUnit,
    }),
    [settings.dateFormat, settings.timezone, columnState, tableSettings.showTime, floatDisplayUnit]
  );

  const getCellValue = useMemo(() => makeGetCellValue(formatterConfig), [formatterConfig]);

  const columnHelper = useMemo(() => createColumnHelper<TreeRow | ScheduleActivity>(), []);
  const dataGridColumns = useMemo(
    () =>
      visibleColumns.map((col, colIndex) => {
        const key = String(col.key);
        const alignClass = 'align' in col && col.align === 'right' ? 'text-right' : '';
        const isFirstColumn = colIndex === 0;
        const floatHeaderLabel =
          key === 'total_float' ? (floatDisplayUnit === 'days' ? 'Total Float (approx. d, 8h/day)' : 'Total Float (hr)')
          : key === 'free_float' ? (floatDisplayUnit === 'days' ? 'Free Float (approx. d, 8h/day)' : 'Free Float (hr)')
          : null;
        const def = columnHelper.accessor((row: TreeRow | ScheduleActivity) => {
          if (row == null) return undefined;
          if (isTreeRow(row)) {
            if (row.type === 'activity') return row.activity != null ? (row.activity as Record<string, unknown>)[key] : undefined;
            return row.node != null ? (row.node as unknown as Record<string, unknown>)[key] : undefined;
          }
          return (row as Record<string, unknown>)[key];
        }, {
          id: key,
          header: floatHeaderLabel ?? col.label,
          cell: ({ row }) => {
            const raw = row.original;
            if (isTreeRow(raw)) {
              if (raw.type === 'wbs') {
                const node = (raw as TreeRow & { type: 'wbs' }).node;
                const level = (raw as TreeRow & { type: 'wbs' }).level;
                const indentPx = level * 20 + 12;
                const isExpanded = expandedWbsIds.has(node.key);
                const count = node.rollupCount ?? getActivitiesInSubtree(node).length;
                if (isFirstColumn) {
                  return (
                    <div
                      className="flex items-center gap-1 min-w-0 overflow-hidden font-medium bg-muted/50 text-foreground"
                      style={{ paddingLeft: indentPx }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWbsExpanded(node.key);
                        }}
                        className="p-0.5 rounded hover:bg-muted flex-shrink-0"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                      <span className="min-w-0 truncate">{node.name || 'WBS'}</span>
                      <span className="text-muted-foreground text-xs tabular-nums shrink-0">({count})</span>
                    </div>
                  );
                }
                return <span className="text-nb-text-muted">—</span>;
              }
              // activity row
              const actRow = raw as TreeRow & { type: 'activity' };
              if (actRow.activity == null) return null;
              const indentPx = actRow.level * 20 + 12 + 24;
              return (
                <span
                  className={cn(getCellClassName(actRow.activity, key), alignClass)}
                  style={{ paddingLeft: indentPx }}
                >
                  {getCellValue(actRow.activity, key)}
                </span>
              );
            }
            const level = (raw as ScheduleActivity & { __wbsLevel?: number }).__wbsLevel;
            const indentPx = groupBy === 'wbs' && level != null ? level * 20 + 12 : 0;
            return (
              <span
                className={cn(getCellClassName(raw as ScheduleActivity, key), alignClass)}
                style={indentPx ? { paddingLeft: indentPx } : undefined}
              >
                {getCellValue(raw as ScheduleActivity, key)}
              </span>
            );
          },
          enableSorting: groupBy !== 'wbs' && col.sortable !== false,
          size: col.width ? parseInt(col.width, 10) || 120 : 120,
          minSize: 80,
        });
        return def;
      }),
    [visibleColumns, getCellValue, groupBy, expandedWbsIds, toggleWbsExpanded, floatDisplayUnit, columnHelper]
  );

  // Removed handleToggleExpanded - no dropdown functionality

  const handleColumnStateChange = useCallback((newState: ColumnState[]) => {
    setColumnState(newState)
  }, [])

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters)
  }, [])

  const handleSearchStatesChange = useCallback((newSearchStates: SearchStates) => {
    setSearchStates(newSearchStates)
  }, [])

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.criticalOnly) count++
    if (filters.dateRange.start || filters.dateRange.end) count++
    if (filters.wbs.length > 0) count++
    if (filters.calendars.length > 0) count++
    if (filters.types.length > 0) count++
    if (filters.statuses.length > 0) count++
    if (filters.udf.length > 0) count++
    return count
  }, [filters])

  if (loading) {
    return (
      <div className="flex-1 min-h-0 rounded-lg border border-border bg-card overflow-hidden p-6">
        <PanelLoading message="Loading activities..." className="min-h-[200px]" />
      </div>
    )
  }

  const toolbarLeftSlot = (
    <>
      <Input
        placeholder="Search activities..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-sm h-8 text-sm"
      />
      <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
        {filteredActivities.length} of {activities.length}
      </span>
    </>
  );

  const toolbarRightSlot = (
    <>
      {/* View controls */}
      <Button
        variant={groupBy === 'none' ? 'default' : 'outline'}
        size="sm"
        onClick={() => startTransition(() => setGroupBy('none'))}
      >
        Flat
      </Button>
      <Button
        variant={groupBy === 'wbs' ? 'default' : 'outline'}
        size="sm"
        onClick={() => startTransition(() => setGroupBy('wbs'))}
      >
        Group by WBS
      </Button>
      {groupBy === 'wbs' && allWbsKeys.size > 0 && (
        <>
          <Button variant="ghost" size="sm" onClick={expandAllWbs} className="text-xs">
            Expand all
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAllWbs} className="text-xs">
            Collapse all
          </Button>
        </>
      )}
      <div className="h-5 w-px bg-border" />
      {/* Configuration */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowFilters(true)}
        className={activeFilterCount > 0 ? 'bg-primary/10 border-primary/30 text-primary' : ''}
      >
        <Filter className="h-3.5 w-3.5 mr-1.5" />
        Filters
        {activeFilterCount > 0 && (
          <span className="ml-1.5 bg-primary text-primary-foreground text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowColumnSettings(true)}
      >
        <Settings2 className="h-3.5 w-3.5 mr-1.5" />
        Columns
      </Button>
      <LayoutButton
        scope={scrollPersistKey}
        currentState={layoutSnapshot}
        onApplyLayout={handleApplyLayout}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Settings
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Table behaviour</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={tableSettings.highlightCritical}
            onCheckedChange={(checked) =>
              setTableSettings((s) => ({ ...s, highlightCritical: checked === true }))
            }
          >
            Highlight critical activities
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={tableSettings.showTime}
            onCheckedChange={(checked) =>
              setTableSettings((s) => ({ ...s, showTime: checked === true }))
            }
          >
            Show time on dates
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="h-5 w-px bg-border" />
      {/* Actions */}
      <Button
        variant="ghost"
        size="sm"
        className="h-8 text-xs"
        onClick={() => toolbarActionsRef.current?.onCsvExport()}
        title="Export CSV"
      >
        <Download className="h-3.5 w-3.5 mr-1" />
        Export CSV
      </Button>
      <div className="h-5 w-px bg-border" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsFullscreen((v) => !v)}
        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </Button>
    </>
  );

  const content = (
    <div className={cn(
      'flex flex-col flex-1 min-h-0 min-w-0 bg-card overflow-hidden',
      !isFullscreen && 'rounded-lg border border-border'
    )}>
      {/* Toolbar row */}
      <div className="flex-none flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {toolbarLeftSlot}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {toolbarRightSlot}
        </div>
      </div>
      {/* Table */}
      <DataGrid<TreeRow | ScheduleActivity>
        data={gridData}
        columns={dataGridColumns}
        density="compact"
        getRowId={(row) => {
          if (row == null) return '';
          return 'id' in row ? String((row as TreeRow).id) : String((row as ScheduleActivity).id);
        }}
        enableQuickFilter={false}
        enableRowSelection={false}
        enableColumnResize={true}
        enableColumnPinning={true}
        pinnedLeftColumnIds={['id']}
        autoFitColumnIds={['id']}
        flexColumnId="wbs_name"
        persistScrollKey={scrollPersistKey}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        enableSorting={groupBy !== 'wbs'}
        enableCopy={false}
        enableCsvExport={true}
        loading={loading}
        emptyMessage="No activities match your filters"
        className="border-0 rounded-none flex-1 min-h-0 min-w-0"
        scrollContainerClassName="flex-1 min-h-0 overflow-auto border-0 rounded-none"
        onRowClick={
          onActivityClick
            ? (row) => {
                const raw = row.original;
                if (isTreeRow(raw) && raw.type === 'activity') {
                  onActivityClick(raw.activity);
                } else if (raw) {
                  onActivityClick(raw as ScheduleActivity);
                }
              }
            : undefined
        }
        getRowClassName={(row) => {
          if (!tableSettings.highlightCritical) return '';
          const raw = row?.original;
          if (raw == null) return '';
          if (isTreeRow(raw)) {
            if (raw.type === 'wbs') return 'bg-muted/40';
            return raw.type === 'activity' && raw.activity?.critical ? 'bg-destructive/10' : '';
          }
          return (raw as ScheduleActivity).critical ? 'bg-destructive/10' : '';
        }}
        getFirstCellClassName={(row) => {
          if (!tableSettings.highlightCritical) return '';
          const raw = row?.original;
          if (raw == null) return '';
          if (isTreeRow(raw)) {
            if (raw.type === 'activity' && raw.activity?.critical) return 'border-l-4 border-l-destructive';
            return '';
          }
          return (raw as ScheduleActivity).critical ? 'border-l-4 border-l-destructive' : '';
        }}
        hideInternalToolbar
        getToolbarActions={(actions) => {
          toolbarActionsRef.current = actions;
        }}
      />
    </div>
  );

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background overflow-hidden">
        {content}
        {showColumnSettings && (
          <ColumnSettings
            columnState={columnState}
            onColumnStateChange={handleColumnStateChange}
            onClose={() => setShowColumnSettings(false)}
            activities={activities}
            udfLabels={udfLabels}
          />
        )}
        {showFilters && (
          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            searchStates={searchStates}
            onSearchStatesChange={handleSearchStatesChange}
            onClose={() => setShowFilters(false)}
            activities={activities}
            udfLabels={udfLabels}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {content}
      {showColumnSettings && (
        <ColumnSettings
          columnState={columnState}
          onColumnStateChange={handleColumnStateChange}
          onClose={() => setShowColumnSettings(false)}
          activities={activities}
          udfLabels={udfLabels}
        />
      )}

      {showFilters && (
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          searchStates={searchStates}
          onSearchStatesChange={handleSearchStatesChange}
          onClose={() => setShowFilters(false)}
          activities={activities}
          udfLabels={udfLabels}
        />
      )}
    </>
  )
}

export default ActivityTable
